import { AcquisitionCostsCalculationAdjustmentCategory, RevenueCalculationAdjustmentCategory, SdcCalculationAdjustmentCategory } from 'models/CalculationAdjustment';
import AmountWithCurrency, { IAmountWithCurrencyDto } from "./AmountWithCurrency"
import { Guid, IApiLink } from "@/types"
import { ApiLinkEntity, ApiLinkRel } from "config/constants"
import dayjs, { Dayjs } from "utils/dayjs"

export enum CalculationDetailsType {
    sale = "sale",
}

// Sale

interface ISpecialDirectCostsBookedValueDto {
    name: string
    ledgerAccount?: string
    amount?: IAmountWithCurrencyDto | null
    snapshotted: boolean
}

interface ISpecialDirectCostsBookedValue {
    name: string
    ledgerAccount?: string
    amount?: AmountWithCurrency | null
    snapshotted: boolean
}

interface ICalculationDetailsAdjustmentDto {
    category: SdcCalculationAdjustmentCategory | RevenueCalculationAdjustmentCategory | AcquisitionCostsCalculationAdjustmentCategory
    value: IAmountWithCurrencyDto
    comment?: string | null
}

export interface ICalculationDetailsAdjustment {
    category: SdcCalculationAdjustmentCategory | RevenueCalculationAdjustmentCategory | AcquisitionCostsCalculationAdjustmentCategory
    value: AmountWithCurrency
    comment?: string | null
}

export interface IRevenueInvoicedDto {
    rentalFees: IAmountWithCurrencyDto
    other: IAmountWithCurrencyDto
    total: IAmountWithCurrencyDto
}

export interface IRevenueInvoiced {
    rentalFees: AmountWithCurrency
    other: AmountWithCurrency
    total: AmountWithCurrency
}



export interface IGetCalculationDetailsBaseSaleDto {
    utilisationPercentage: number | null
    utilisationPercentageNotSnapshotted: number | null
    resultOfInvoicedCustomerRequests: IAmountWithCurrencyDto | null
    revenueInvoiced?: IRevenueInvoicedDto | null
    revenueInvoicedTotal: IAmountWithCurrencyDto | null
    revenueInvoicedNotSnapshotted?: IRevenueInvoicedDto | null
    depreciationInvoiced: IAmountWithCurrencyDto | null
    imputedInterestInvoiced: IAmountWithCurrencyDto | null
    specialDirectCostsBooked: IAmountWithCurrencyDto | null
    specialDirectCostsBookedValues: ISpecialDirectCostsBookedValueDto[] | null
    invoicedOverheadCosts: IAmountWithCurrencyDto | null
    invoicedOverheadWarrantyValue: IAmountWithCurrencyDto | null
    invoicedOverheadAdministrationValue: IAmountWithCurrencyDto | null
    invoicedOverheadSalesValue: IAmountWithCurrencyDto | null
    impactOfNotUsage: IAmountWithCurrencyDto | null
    depreciationNotInvoiced: IAmountWithCurrencyDto | null
    imputedInterestNotInvoiced: IAmountWithCurrencyDto | null
    salesResult: IAmountWithCurrencyDto
    revenue: {
        revenue: IAmountWithCurrencyDto,
        additionalRevenue: {
            total: IAmountWithCurrencyDto
            entities: ICalculationDetailsAdjustmentDto[]
        } | null,
        total: IAmountWithCurrencyDto
    }
    sdc: {
        total: IAmountWithCurrencyDto | null
        entities: ICalculationDetailsAdjustmentDto[],

    } | null
    bookValueIFRS: {
        bookValueIFRS: IAmountWithCurrencyDto,
        additionalAcquisitionCosts: {
            incomingInvestmentInvoices: IAmountWithCurrencyDto | null,
            total: IAmountWithCurrencyDto,
            entities: ICalculationDetailsAdjustmentDto[]
        } | null,
        total: IAmountWithCurrencyDto
    }
    overheadCosts: IAmountWithCurrencyDto | null
    overheadWarrantyValue: IAmountWithCurrencyDto | null
    overheadAdministrationValue: IAmountWithCurrencyDto | null
    overheadSalesValue: IAmountWithCurrencyDto | null
    totalResult: IAmountWithCurrencyDto
    totalReturnPercentage: number
    totalRevenue: IAmountWithCurrencyDto
    totalAcquisitionProductionCosts: IAmountWithCurrencyDto | null
    listPrice: IAmountWithCurrencyDto | null
    calculatedSalesPrice: IAmountWithCurrencyDto | null
    discountPercentage: number | null
    listPriceMissingForSomeAssets: boolean
}

export interface IGetCalculationDetailsSaleDto extends IGetCalculationDetailsBaseSaleDto {
    invoicedOverheadWarrantyPercentage: number | null
    invoicedOverheadAdministrationPercentage: number | null
    invoicedOverheadSalesPercentage: number | null
    overheadWarrantyPercentage: number | null
    overheadAdministrationPercentage: number | null
    overheadSalesPercentage: number | null
    nodeAssetConstructionYear: number | null
    nodeAssetAcquisitionDate: string | null
    links?: IApiLink[]
}

export interface IGetCalculationDetailsSaleExtendedAssetDto extends IGetCalculationDetailsBaseSaleDto {
    assetId: Guid
    lmid: string
}
export interface IGetCalculationDetailsSaleExtendedArticleDto extends IGetCalculationDetailsBaseSaleDto {
    articleId: Guid
    articleDescription: string
    assets: IGetCalculationDetailsSaleExtendedAssetDto[]
}

export interface IGetCalculationDetailsSaleExtendedDto extends IGetCalculationDetailsBaseSaleDto {
    articles: IGetCalculationDetailsSaleExtendedArticleDto[]
}


export class CalculationDetailsBaseSale {
    utilisationPercentage: number | null
    utilisationPercentageNotSnapshotted: number | null
    resultOfInvoicedCustomerRequests: AmountWithCurrency | null
    revenueInvoiced: IRevenueInvoiced | null
    revenueInvoicedTotal: AmountWithCurrency | null
    revenueInvoicedNotSnapshotted: IRevenueInvoiced | null
    depreciationInvoiced: AmountWithCurrency | null
    imputedInterestInvoiced: AmountWithCurrency | null
    specialDirectCostsBooked: AmountWithCurrency | null
    specialDirectCostsBookedValues: ISpecialDirectCostsBookedValue[] | null
    invoicedOverheadCosts: AmountWithCurrency | null
    invoicedOverheadWarrantyValue: AmountWithCurrency | null
    invoicedOverheadAdministrationValue: AmountWithCurrency | null
    invoicedOverheadSalesValue: AmountWithCurrency | null
    impactOfNotUsage: AmountWithCurrency | null
    depreciationNotInvoiced: AmountWithCurrency | null
    imputedInterestNotInvoiced: AmountWithCurrency | null
    salesResult: AmountWithCurrency
    revenue: {
        revenue: AmountWithCurrency,
        additionalRevenue?: {
            total: AmountWithCurrency,
            entities: ICalculationDetailsAdjustment[]
        } | null,
        total: AmountWithCurrency
    }
    sdc: {
        total: AmountWithCurrency | null
        entities: ICalculationDetailsAdjustment[],
    } | null
    bookValueIFRS: {
        bookValueIFRS: AmountWithCurrency,
        additionalAcquisitionCosts?: {
            incomingInvestmentInvoices: AmountWithCurrency | null
            total: AmountWithCurrency
            entities: ICalculationDetailsAdjustment[]
        } | null,
        total: AmountWithCurrency
    }
    overheadCosts: AmountWithCurrency | null
    overheadWarrantyValue: AmountWithCurrency | null
    overheadAdministrationValue: AmountWithCurrency | null
    overheadSalesValue: AmountWithCurrency | null
    totalResult: AmountWithCurrency
    totalReturnPercentage: number
    totalRevenue: AmountWithCurrency
    totalAcquisitionProductionCosts: AmountWithCurrency | null
    listPrice: AmountWithCurrency | null
    calculatedSalesPrice: AmountWithCurrency | null
    discountPercentage: number | null
    listPriceMissingForSomeAssets: boolean

    constructor(dto: IGetCalculationDetailsBaseSaleDto) {
        this.utilisationPercentage = dto.utilisationPercentage
        this.utilisationPercentageNotSnapshotted = dto.utilisationPercentageNotSnapshotted
        this.resultOfInvoicedCustomerRequests = dto.resultOfInvoicedCustomerRequests === null ? null : new AmountWithCurrency(dto.resultOfInvoicedCustomerRequests)
        this.revenueInvoiced = !dto.revenueInvoiced ? null : {
            rentalFees: new AmountWithCurrency(dto.revenueInvoiced.rentalFees),
            other: new AmountWithCurrency(dto.revenueInvoiced.other),
            total: new AmountWithCurrency(dto.revenueInvoiced.total)
        }
        this.revenueInvoicedTotal = dto.revenueInvoicedTotal === null ? null : new AmountWithCurrency(dto.revenueInvoicedTotal)
        this.revenueInvoicedNotSnapshotted = !dto.revenueInvoicedNotSnapshotted ? null : {
            rentalFees: new AmountWithCurrency(dto.revenueInvoicedNotSnapshotted.rentalFees),
            other: new AmountWithCurrency(dto.revenueInvoicedNotSnapshotted.other),
            total: new AmountWithCurrency(dto.revenueInvoicedNotSnapshotted.total)
        }
        this.depreciationInvoiced = dto.depreciationInvoiced === null ? null : new AmountWithCurrency(dto.depreciationInvoiced)
        this.imputedInterestInvoiced = dto.imputedInterestInvoiced === null ? null : new AmountWithCurrency(dto.imputedInterestInvoiced)
        this.specialDirectCostsBooked = dto.specialDirectCostsBooked === null ? null : new AmountWithCurrency(dto.specialDirectCostsBooked)
        this.specialDirectCostsBookedValues = dto.specialDirectCostsBookedValues?.map(sdc => ({
            name: sdc.name,
            ledgerAccount: sdc.ledgerAccount,
            amount: sdc?.amount ? new AmountWithCurrency(sdc.amount) : null,
            snapshotted: sdc.snapshotted
        })) || null
        this.invoicedOverheadCosts = dto.invoicedOverheadCosts === null ? null : new AmountWithCurrency(dto.invoicedOverheadCosts)
        this.invoicedOverheadWarrantyValue = dto.invoicedOverheadWarrantyValue === null ? null : new AmountWithCurrency(dto.invoicedOverheadWarrantyValue)
        this.invoicedOverheadAdministrationValue = dto.invoicedOverheadAdministrationValue === null ? null : new AmountWithCurrency(dto.invoicedOverheadAdministrationValue)
        this.invoicedOverheadSalesValue = dto.invoicedOverheadSalesValue === null ? null : new AmountWithCurrency(dto.invoicedOverheadSalesValue)
        this.impactOfNotUsage = dto.impactOfNotUsage === null ? null : new AmountWithCurrency(dto.impactOfNotUsage)
        this.depreciationNotInvoiced = dto.depreciationNotInvoiced === null ? null : new AmountWithCurrency(dto.depreciationNotInvoiced)
        this.imputedInterestNotInvoiced = dto.imputedInterestNotInvoiced === null ? null : new AmountWithCurrency(dto.imputedInterestNotInvoiced)
        this.salesResult = new AmountWithCurrency(dto.salesResult)
        this.revenue = {
            revenue: new AmountWithCurrency(dto.revenue.revenue),
            additionalRevenue: dto.revenue.additionalRevenue === null ? null :
                {
                    total: new AmountWithCurrency(dto.revenue.additionalRevenue.total),
                    entities: dto.revenue.additionalRevenue.entities.map(adj => ({
                        category: adj.category,
                        value: new AmountWithCurrency(adj.value),
                        comment: adj.comment
                    })),
                },
            total: new AmountWithCurrency(dto.revenue.total)
        }
        this.sdc = !dto.sdc ? null : {
            entities: dto.sdc.entities.map(adj => ({ category: adj.category, value: new AmountWithCurrency(adj.value), comment: adj.comment })),
            total: dto.sdc.total === null ? null : new AmountWithCurrency(dto.sdc.total)
        }
        this.bookValueIFRS = {
            bookValueIFRS: new AmountWithCurrency(dto.bookValueIFRS.bookValueIFRS),
            additionalAcquisitionCosts: dto.bookValueIFRS.additionalAcquisitionCosts === null ? null : {
                incomingInvestmentInvoices: dto.bookValueIFRS.additionalAcquisitionCosts.incomingInvestmentInvoices === null ? null : new AmountWithCurrency(dto.bookValueIFRS.additionalAcquisitionCosts.incomingInvestmentInvoices),
                total: new AmountWithCurrency(dto.bookValueIFRS.additionalAcquisitionCosts.total),
                entities: dto.bookValueIFRS.additionalAcquisitionCosts.entities.map(adj => ({
                    category: adj.category,
                    value: new AmountWithCurrency(adj.value),
                    comment: adj.comment
                }))
            },
            total: new AmountWithCurrency(dto.bookValueIFRS.total)
        }
        this.overheadCosts = dto.overheadCosts === null ? null : new AmountWithCurrency(dto.overheadCosts)
        this.overheadWarrantyValue = dto.overheadWarrantyValue === null ? null : new AmountWithCurrency(dto.overheadWarrantyValue)
        this.overheadAdministrationValue = dto.overheadAdministrationValue === null ? null : new AmountWithCurrency(dto.overheadAdministrationValue)
        this.overheadSalesValue = dto.overheadSalesValue === null ? null : new AmountWithCurrency(dto.overheadSalesValue)
        this.totalResult = new AmountWithCurrency(dto.totalResult)
        this.totalReturnPercentage = dto.totalReturnPercentage
        this.totalRevenue = new AmountWithCurrency(dto.totalRevenue)
        this.totalAcquisitionProductionCosts = dto.totalAcquisitionProductionCosts === null ? null : new AmountWithCurrency(dto.totalAcquisitionProductionCosts)
        this.listPrice = dto.listPrice === null ? null : new AmountWithCurrency(dto.listPrice)
        this.calculatedSalesPrice = dto.calculatedSalesPrice === null ? null : new AmountWithCurrency(dto.calculatedSalesPrice)
        this.discountPercentage = dto.discountPercentage ?? null
        this.listPriceMissingForSomeAssets = dto.listPriceMissingForSomeAssets

    }
}

export class CalculationDetailsSale extends CalculationDetailsBaseSale {
    invoicedOverheadWarrantyPercentage: number | null
    invoicedOverheadAdministrationPercentage: number | null
    invoicedOverheadSalesPercentage: number | null
    overheadWarrantyPercentage: number | null
    overheadAdministrationPercentage: number | null
    overheadSalesPercentage: number | null
    linkGetCalculationDetailsExtended?: IApiLink
    linkExportCalculationDetails?: IApiLink
    nodeAssetConstructionYear: number | null
    nodeAssetAcquisitionDate: Dayjs | null

    constructor(dto: IGetCalculationDetailsSaleDto) {
        super(dto)
        this.invoicedOverheadWarrantyPercentage = dto.invoicedOverheadWarrantyPercentage
        this.invoicedOverheadAdministrationPercentage = dto.invoicedOverheadAdministrationPercentage
        this.invoicedOverheadSalesPercentage = dto.invoicedOverheadSalesPercentage
        this.overheadWarrantyPercentage = dto.overheadWarrantyPercentage
        this.overheadAdministrationPercentage = dto.overheadAdministrationPercentage
        this.overheadSalesPercentage = dto.overheadSalesPercentage
        this.linkGetCalculationDetailsExtended = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.calculationDetailsExtended && lnk.rel === ApiLinkRel.self)
        this.linkExportCalculationDetails = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.salesCalculationReport && lnk.rel === ApiLinkRel.self)
        this.nodeAssetAcquisitionDate = dto.nodeAssetAcquisitionDate === null ? null : dayjs(dto.nodeAssetAcquisitionDate)
        this.nodeAssetConstructionYear = dto.nodeAssetConstructionYear
    }
}

export class CalculationDetailsSaleExtendedAsset extends CalculationDetailsBaseSale {
    assetId: Guid
    lmid: string

    constructor(dto: IGetCalculationDetailsSaleExtendedAssetDto) {
        super(dto)
        this.assetId = dto.assetId
        this.lmid = dto.lmid
    }

}

export class CalculationDetailsSaleExtendedArticle extends CalculationDetailsBaseSale {
    articleId: Guid
    articleDescription: string
    assets: CalculationDetailsSaleExtendedAsset[]

    constructor(dto: IGetCalculationDetailsSaleExtendedArticleDto) {
        super(dto)
        this.articleId = dto.articleId
        this.articleDescription = dto.articleDescription
        this.assets = dto.assets.map(_dto => new CalculationDetailsSaleExtendedAsset(_dto))
    }
}
export class CalculationDetailsSaleExtended extends CalculationDetailsBaseSale {
    articles: CalculationDetailsSaleExtendedArticle[]
    constructor(dto: IGetCalculationDetailsSaleExtendedDto) {
        super(dto)
        this.articles = dto.articles.map(_dto => new CalculationDetailsSaleExtendedArticle(_dto))
    }
}
